/**
 * Coalesces sleep/wake signals (visibility, focus, online, pageshow) into a single
 * debounced recovery flush so multiple browser events do not stack duplicate work.
 */

export type WakeSignal =
  | "visibility_visible"
  | "focus"
  | "online"
  | "pageshow";

export type WakeRecoveryContext = {
  /** Deduped signals for this flush cycle, stable order */
  signals: WakeSignal[];
  /** Monotonic id for correlating logs */
  cycleId: number;
};

export type WakeRecoveryJob = (ctx: WakeRecoveryContext) => void;

export type WakeRecoveryLogger = {
  debug: (data: Record<string, unknown>, message: string) => void;
};

const NO_OP_LOGGER: WakeRecoveryLogger = {
  debug: () => {},
};

let logger: WakeRecoveryLogger = NO_OP_LOGGER;

/** Default debounce after the last signal before running registered jobs (ms). */
const DEFAULT_DEBOUNCE_MS = 550;

/** Stable execution order for known jobs; unknown keys run after, sorted. */
const JOB_ORDER: readonly string[] = [
  "network-status",
  "connection-status-distraction",
  "web-timer-resume",
  "desktop-foreground-session",
  "realtime-resume",
  "chat-resume",
  "tasks-resume",
];

const jobs = new Map<string, WakeRecoveryJob>();

let debounceMs = DEFAULT_DEBOUNCE_MS;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSignals: WakeSignal[] = [];
let cycleSeq = 0;
let isFlushing = false;
let flushAgainAfterCurrent = false;

// ---------------------------------------------------------------------------
// Metrics (in-memory; dev / debugging)
// ---------------------------------------------------------------------------

export type WakeRecoveryCycleRecord = {
  cycleId: number;
  signals: WakeSignal[];
  debounceMs: number;
  wallMs: number;
  handlerWallMs: Record<string, number>;
  notifyCountBeforeFlush: number;
};

const MAX_CYCLE_HISTORY = 12;
const cycleHistory: WakeRecoveryCycleRecord[] = [];

let totalNotifyCalls = 0;
let totalFlushes = 0;

function pushCycleRecord(rec: WakeRecoveryCycleRecord): void {
  cycleHistory.push(rec);
  if (cycleHistory.length > MAX_CYCLE_HISTORY) {
    cycleHistory.shift();
  }
}

export function getWakeRecoveryMetricsSnapshot(): {
  totalNotifyCalls: number;
  totalFlushes: number;
  recentCycles: WakeRecoveryCycleRecord[];
} {
  return {
    totalNotifyCalls,
    totalFlushes,
    recentCycles: [...cycleHistory],
  };
}

/** Test helper */
export function resetWakeRecoveryMetricsForTests(): void {
  totalNotifyCalls = 0;
  totalFlushes = 0;
  cycleHistory.length = 0;
}

// ---------------------------------------------------------------------------

export function setWakeRecoveryLogger(next: WakeRecoveryLogger | null): void {
  logger = next ?? NO_OP_LOGGER;
}

export function setWakeRecoveryDebounceMs(ms: number): void {
  debounceMs = Math.max(0, ms);
}

export function registerWakeRecoveryJob(
  key: string,
  job: WakeRecoveryJob,
): void {
  jobs.set(key, job);
}

export function unregisterWakeRecoveryJob(key: string): void {
  jobs.delete(key);
}

function dedupeSignalOrder(signals: WakeSignal[]): WakeSignal[] {
  const seen = new Set<WakeSignal>();
  const out: WakeSignal[] = [];
  for (const s of signals) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function orderedJobKeys(): string[] {
  const known: string[] = [];
  for (const k of JOB_ORDER) {
    if (jobs.has(k)) known.push(k);
  }
  const rest = [...jobs.keys()]
    .filter((k) => !JOB_ORDER.includes(k))
    .sort();
  return [...known, ...rest];
}

function scheduleFlush(): void {
  if (typeof setTimeout === "undefined") {
    flushWakeRecoveryNow();
    return;
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    flushWakeRecoveryNow();
  }, debounceMs);
}

/**
 * Bumps the wake recovery debouncer. Multiple rapid calls (focus + visibility + online)
 * collapse into one flush after `debounceMs` quiet period.
 */
export function notifyWakeSignals(
  signal: WakeSignal | WakeSignal[],
): void {
  const batch = Array.isArray(signal) ? signal : [signal];
  pendingSignals.push(...batch);
  totalNotifyCalls += 1;

  logger.debug(
    {
      signals: batch,
      pendingLength: pendingSignals.length,
    },
    "[wakeRecovery] notifyWakeSignals",
  );

  if (isFlushing) {
    flushAgainAfterCurrent = true;
    return;
  }
  scheduleFlush();
}

function flushWakeRecoveryNow(): void {
  if (pendingSignals.length === 0) {
    return;
  }

  isFlushing = true;
  const signals = dedupeSignalOrder(pendingSignals);
  pendingSignals = [];
  const notifyBatchSize = signals.length;
  const cycleId = ++cycleSeq;
  totalFlushes += 1;

  const wallStart = typeof performance !== "undefined" ? performance.now() : 0;
  const handlerWallMs: Record<string, number> = {};

  logger.debug(
    {
      cycleId,
      signals,
      jobCount: jobs.size,
      debounceMs,
      notifyBatchSize,
    },
    "[wakeRecovery] flush start",
  );

  const keys = orderedJobKeys();
  for (const key of keys) {
    const job = jobs.get(key);
    if (!job) continue;
    const h0 = typeof performance !== "undefined" ? performance.now() : 0;
    try {
      job({ signals, cycleId });
    } catch (error) {
      logger.debug(
        {
          cycleId,
          key,
          error: error instanceof Error ? error.message : String(error),
        },
        "[wakeRecovery] job threw",
      );
    }
    if (typeof performance !== "undefined") {
      handlerWallMs[key] = Math.round(performance.now() - h0);
    }
  }

  const wallMs =
    typeof performance !== "undefined"
      ? Math.round(performance.now() - wallStart)
      : 0;

  pushCycleRecord({
    cycleId,
    signals,
    debounceMs,
    wallMs,
    handlerWallMs,
    notifyCountBeforeFlush: notifyBatchSize,
  });

  logger.debug(
    {
      cycleId,
      signals,
      wallMs,
      handlerWallMs,
    },
    "[wakeRecovery] flush complete",
  );

  isFlushing = false;

  if (flushAgainAfterCurrent || pendingSignals.length > 0) {
    flushAgainAfterCurrent = false;
    if (pendingSignals.length > 0) {
      scheduleFlush();
    }
  }
}
